import LogManager from "../../utils/logManager";
import { callRouterLambda } from "../../utils/routerLambdaUtils";
import {
  INSIGHT_KIND,
  PINNED_INSIGHT_KINDS,
  REGULAR_COLLABORATOR_MIN_MEETINGS,
  WHOS_WHO_SECTION_CONFIG,
  WHOS_WHO_SECTION_ID,
} from "./whosWho.constants";

const hasValidPeople = (people) => Array.isArray(people)
  && people.length > 0
  && people.every(
    (person) => typeof person === "string" && person.trim().length > 0
  );

const isDisplayableInsight = (insight) => {
  if (!insight) return false;

  switch (insight.kind) {
    case INSIGHT_KIND.NEW_AMAZONIAN:
    case INSIGHT_KIND.RECENTLY_PROMOTED:
    case INSIGHT_KIND.NEW_ROLE:
    case INSIGHT_KIND.NEW_TEAM:
      return true;
    case INSIGHT_KIND.ANNIVERSARY:
      return Number.isInteger(insight.years) && insight.years > 0;
    case INSIGHT_KIND.NETWORK_CONNECTION:
    case INSIGHT_KIND.MUTUAL_COLLEAGUES:
      return hasValidPeople(insight.people);
    case INSIGHT_KIND.SHARED_REPORTING:
      return typeof insight.managerName === "string"
        && Boolean(insight.managerName.trim())
        && (insight.managerRole == null || typeof insight.managerRole === "string");
    default:
      return false;
  }
};

/**
 * Keeps the first available pinned insight in the always-visible card area and preserves backend
 * order for every remaining supported insight in expanded details.
 */
export const classifyWhosWhoInsights = (insights = []) => {
  const displayableInsights = Array.isArray(insights)
    ? insights.filter(isDisplayableInsight)
    : [];

  const pinnedIndex = displayableInsights.findIndex(
    (insight) => PINNED_INSIGHT_KINDS.includes(insight.kind)
  );
  const pinnedInsight = pinnedIndex >= 0 ? displayableInsights[pinnedIndex] : null;

  return {
    pinnedInsight,
    expandedInsights: displayableInsights.filter((_, index) => index !== pinnedIndex),
  };
};

export const formatWhosWhoCountLabel = (label, count) => `${label} (${count})`;

const sectionIdFor = (person) => {
  if (person.external) return WHOS_WHO_SECTION_ID.EXTERNAL_PARTICIPANTS;
  if (person.isYou) return WHOS_WHO_SECTION_ID.YOU;

  const meetingCount = person.metBeforeCount ?? 0;
  if (meetingCount <= 0) {
    return WHOS_WHO_SECTION_ID.FIRST_TIME_MEETING;
  }
  if (meetingCount < REGULAR_COLLABORATOR_MIN_MEETINGS) {
    return WHOS_WHO_SECTION_ID.MET_BEFORE;
  }
  return WHOS_WHO_SECTION_ID.REGULAR_COLLABORATORS;
};

/**
 * Groups already-filtered V2 cards into the configured display sections without changing their
 * order within a section.
 *
 * @param {Array<Object>} invitees - Cards remaining after organization filtering.
 * @returns {Array<{id: string, label: string, invitees: Array<Object>}>} Ordered sections.
 */
export const buildWhosWhoSections = (invitees = []) => {
  const inviteesBySection = WHOS_WHO_SECTION_CONFIG.reduce((bySection, { id }) => {
    bySection[id] = [];
    return bySection;
  }, {});

  invitees.forEach((person) => {
    inviteesBySection[sectionIdFor(person)].push(person);
  });

  return WHOS_WHO_SECTION_CONFIG.map((section) => ({
    ...section,
    invitees: inviteesBySection[section.id],
  }));
};

/**
 * Parses the `displayName|email` wire format into card-shaped objects, for the degraded list the
 * panel shows when /whos-who fails.
 *
 * @param {string} externalInvitees - Comma-separated `displayName|email` entries.
 * @returns {Array<Object>} Card-shaped objects with external: true.
 */
export const parseExternalInvitees = (externalInvitees) =>
  (externalInvitees || "")
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => {
      const pipe = entry.indexOf("|");
      const displayName = pipe > 0 ? entry.slice(0, pipe).trim() : "";
      const email = (pipe >= 0 ? entry.slice(pipe + 1) : entry).trim();
      return { displayName, email };
    })
    .filter(({ email }) => email)
    .map(({ displayName, email }) => {
      const name = displayName || email;
      return { id: email, name, email, external: true, initials: name.slice(0, 2).toUpperCase() };
    });

/**
 * Calls the /whos-who endpoint to fetch invitee profile cards.
 *
 * Internal and external invitees travel separately and in different formats, matching the split
 * the availability handler makes by email domain.
 *
 * @param {Object} params
 * @param {string} params.meetingUuid - Meeting UUID from the meeting session.
 * @param {string} params.attendees - Comma-separated aliases of the internal (Amazon) invitees
 * @param {string} params.externalInvitees - Comma-separated `displayName|email` entries for
 *   non-Amazon invitees.
 * @returns {Promise<Object>} Parsed response with { invitees: [...] }
 * @throws {Error} On network failure, non-200 response, or an in-band error payload.
 */
export const fetchWhosWho = async ({ meetingUuid, attendees, externalInvitees }) => {
  try {
    const payload = await callRouterLambda("/whos-who", { meetingUuid, attendees, externalInvitees });

    if (payload?.error) {
      throw new Error(`/whos-who returned error: ${payload.error}`);
    }

    return payload;
  } catch (error) {
    LogManager.error("Error calling /whos-who:", error);
    throw error;
  }
};
